import React, { useEffect, useState } from 'react';
import { FileIcon, XIcon, PlayIcon, CheckSquare, Square, CoinsIcon, ClockIcon, AudioLinesIcon, VideoIcon, FileTextIcon, CheckCircle2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { useScreenInit } from '../useScreenInit.js';
type Phase = 'idle' | 'scanning' | 'done';
export function IngestPage() {
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const c = copy.ingest;
  const [phase, setPhase] = useState<Phase>(screenInit?.phase || 'idle');
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const allFiles = c.tree;
  const allEstimateFiles = c.estimateGroups.flatMap(g => g.files);
  const allEstimateIds = allEstimateFiles.map(f => f.id);
  const [selected, setSelected] = useState<string[]>(allEstimateIds);
  useEffect(() => {
    if (phase !== 'scanning') return;
    let pct = 0;
    const interval = setInterval(() => {
      pct += 4;
      setProgress(Math.min(pct, 100));
      setCurrentFile(allFiles[Math.floor(pct / 100 * allFiles.length) % allFiles.length]?.name || '');
      if (pct >= 100) {
        clearInterval(interval);
        setPhase('done');
      }
    }, 120);
    return () => clearInterval(interval);
  }, [phase, allFiles]);
  const startScan = () => {
    setProgress(0);
    setPhase('scanning');
  };
  const cancelScan = () => {
    setPhase('idle');
    setProgress(0);
  };
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSelected = selected.length === allEstimateIds.length;
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <VideoIcon size={18} className="text-purple-500" data-id="element-368" />;
      case 'audio':
        return <AudioLinesIcon size={18} className="text-amber-500" data-id="element-369" />;
      case 'doc':
        return <FileTextIcon size={18} className="text-blue-500" data-id="element-370" />;
      default:
        return <FileIcon size={18} className="text-slate-400" data-id="element-371" />;
    }
  };
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-372">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case" data-id="element-373" />

      <main className="mx-auto max-w-4xl px-6 py-12 space-y-8" data-id="element-374">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-375">
          <div data-id="element-376">
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-377">
              {c.title}
            </h1>
            <p className="text-base text-slate-500 dark:text-[#a3a3a3] mt-2 leading-relaxed" data-id="element-378">
              {c.subtitle}
            </p>
          </div>
          {phase === 'idle' && <button onClick={startScan} className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-[0.98] transition-all shadow-sm hover:shadow-md" data-id="element-379">
              <PlayIcon size={17} strokeWidth={2.5} data-id="element-380" />
              {c.scanButton}
            </button>}
        </div>

        {/* Uploaded Files List */}
        <section className="rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-6 shadow-sm" data-id="element-381">
          <div className="flex items-center justify-between" data-id="element-382">
            <div data-id="element-383">
              <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-384">
                {c.pickerTitle}
              </h2>
              <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-0.5" data-id="element-385">
                {c.pickerHint}
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30 rounded-full" data-id="element-386">
              <CheckCircle2Icon size={14} className="text-green-600 dark:text-green-400" data-id="element-387" />
              <span className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wider" data-id="element-388">
                Upload Complete
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-389">
            {c.tree.map((file, idx) => <div key={idx} className="flex items-center gap-3 p-3 bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] rounded-2xl shadow-sm hover:border-slate-300 dark:hover:border-[#444] transition-all group" data-id="element-390">
                <div className="p-2 rounded-xl bg-slate-50 dark:bg-[#262626] group-hover:bg-slate-100 dark:group-hover:bg-[#333] transition-colors" data-id="element-391">
                  {getFileIcon(file.type)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-392">
                  <div className="text-sm font-semibold text-slate-700 dark:text-[#e5e5e5] truncate" data-id="element-393">
                    {file.name}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-394">
                    {file.type}
                  </div>
                </div>
              </div>)}
          </div>

          <div className="flex items-start gap-3 text-xs text-slate-500 dark:text-[#a3a3a3] leading-relaxed bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-900/20 rounded-2xl p-4" data-id="element-395">
            <div className="mt-0.5 p-1 bg-blue-100 dark:bg-blue-900/30 rounded-md text-blue-600 dark:text-blue-400" data-id="element-396">
              <ClockIcon size={12} data-id="element-397" />
            </div>
            {c.cheapPassNote}
          </div>

          {phase === 'scanning' && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-4 pt-2" data-id="element-398">
              <div className="space-y-3" data-id="element-399">
                <div className="flex items-center justify-between text-sm" data-id="element-400">
                  <div className="flex items-center gap-2" data-id="element-401">
                    <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" data-id="element-402" />
                    <span className="font-semibold text-slate-900 dark:text-white" data-id="element-403">
                      {c.scanningLabel}
                    </span>
                  </div>
                  <span className="font-bold text-blue-600 dark:text-blue-400 tabular-nums" data-id="element-404">
                    {progress}%
                  </span>
                </div>
                <div className="h-3 w-full bg-slate-200 dark:bg-[#333] rounded-full overflow-hidden" data-id="element-405">
                  <motion.div className="h-full bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" initial={{
                width: 0
              }} animate={{
                width: `${progress}%`
              }} transition={{
                duration: 0.3
              }} data-id="element-406" />
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-[#a3a3a3] font-mono bg-white dark:bg-[#1a1a1a] p-2 rounded-lg border border-slate-200 dark:border-[#333]" data-id="element-407">
                  <span className="text-blue-500 dark:text-blue-400 font-bold" data-id="element-408">
                    SCANNING:
                  </span>
                  <span className="truncate" data-id="element-409">{currentFile}</span>
                </div>
              </div>
              <button onClick={cancelScan} className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-[#333] text-slate-600 dark:text-[#a3a3a3] rounded-xl font-medium hover:bg-slate-100 dark:hover:bg-white/5 active:scale-[0.98] transition-all" data-id="element-410">
                <XIcon size={16} strokeWidth={2.5} data-id="element-411" />
                {c.cancelLabel}
              </button>
            </motion.div>}
        </section>

        {/* Estimate panel */}
        <AnimatePresence data-id="element-412">
          {phase === 'done' && <motion.section initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-6 shadow-sm" data-id="element-413">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-414">
                <div data-id="element-415">
                  <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-416">
                    {c.estimateTitle}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-0.5" data-id="element-417">
                    {c.estimateSubtitle}
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-bold shadow-sm" data-id="element-418">
                  <CoinsIcon size={14} data-id="element-419" />
                  {c.estimate.costLow}–{c.estimate.costHigh}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-420">
                <div className="rounded-2xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4 shadow-sm" data-id="element-421">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-422">
                    <AudioLinesIcon size={14} strokeWidth={2} data-id="element-423" />
                    Files
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-424">
                    {c.estimate.spokenAudioCount}
                  </div>
                </div>
                <div className="rounded-2xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4 shadow-sm" data-id="element-425">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-426">
                    <ClockIcon size={14} strokeWidth={2} data-id="element-427" />
                    Duration
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-428">
                    {c.estimate.totalDuration}
                  </div>
                </div>
                <div className="rounded-2xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4 shadow-sm" data-id="element-429">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-430">
                    Tokens
                  </div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-431">
                    {c.estimate.tokenRangeLow}
                  </div>
                </div>
                <div className="rounded-2xl bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-[#333] p-4 shadow-sm" data-id="element-432">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-[#a3a3a3] text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-433">
                    <CoinsIcon size={14} strokeWidth={2} data-id="element-434" />
                    Cost
                  </div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white tabular-nums" data-id="element-435">
                    {c.estimate.costHigh}
                  </div>
                </div>
              </div>

              <div className="space-y-4" data-id="element-436">
                <div className="flex items-center justify-between px-2" data-id="element-437">
                  <label className="flex items-center gap-3 text-sm font-semibold text-slate-700 dark:text-[#e5e5e5] cursor-pointer select-none hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-438">
                    <button onClick={() => setSelected(allSelected ? [] : allEstimateIds)} className="text-slate-400 dark:text-[#555] hover:text-blue-500 dark:hover:text-blue-400 transition-colors" data-id="element-439">
                      {allSelected ? <CheckSquare size={20} className="text-blue-500" strokeWidth={2.5} data-id="element-440" /> : <Square size={20} strokeWidth={2} data-id="element-441" />}
                    </button>
                    {c.selectAllLabel}
                  </label>
                  <span className="text-xs font-bold text-slate-400 dark:text-[#555] uppercase tracking-widest" data-id="element-442">
                    {selected.length} Selected
                  </span>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] overflow-hidden shadow-sm" data-id="element-443">
                  <div className="divide-y divide-slate-100 dark:divide-[#262626]" data-id="element-444">
                    {allEstimateFiles.map(file => {
                  const isSelected = selected.includes(file.id);
                  return <label key={file.id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer transition-colors group" data-id="element-445">
                          <span className="flex items-center gap-3" data-id="element-446">
                            <button onClick={e => {
                        e.preventDefault();
                        toggle(file.id);
                      }} className="text-slate-400 dark:text-[#555] group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" data-id="element-447">
                              {isSelected ? <CheckSquare size={18} className="text-blue-500" strokeWidth={2.5} data-id="element-448" /> : <Square size={18} strokeWidth={2} data-id="element-449" />}
                            </button>
                            <span className="text-sm font-medium text-slate-700 dark:text-[#e5e5e5] group-hover:text-slate-900 dark:group-hover:text-white transition-colors" data-id="element-450">
                              {file.name}
                            </span>
                          </span>
                          <div className="flex items-center gap-3" data-id="element-451">
                            <span className="text-xs font-semibold text-slate-400 dark:text-[#555] tabular-nums bg-slate-100 dark:bg-[#262626] px-2 py-1 rounded-md" data-id="element-452">
                              {file.duration}
                            </span>
                          </div>
                        </label>;
                })}
                  </div>
                </div>
              </div>

              <button disabled={selected.length === 0} onClick={() => navigate('/case/files')} className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-2xl font-bold active:scale-[0.98] transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50" data-id="element-453">
                <PlayIcon size={18} strokeWidth={2.5} data-id="element-454" />
                {c.confirmButton} ({selected.length})
              </button>
            </motion.section>}
        </AnimatePresence>
      </main>
    </div>;
}